import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Upload, Image, Globe, Save, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminSiteSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [settings, setSettings] = useState({
    logo_url: "",
    favicon_url: "",
    footer_email: "",
    footer_phone: "",
    footer_address: "",
    footer_city: "",
    social_facebook: "",
    social_instagram: "",
    social_linkedin: ""
  });

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        logo_url: res.data.logo_url || "",
        favicon_url: res.data.favicon_url || "",
        footer_email: res.data.footer_email || "",
        footer_phone: res.data.footer_phone || "",
        footer_address: res.data.footer_address || "",
        footer_city: res.data.footer_city || "",
        social_facebook: res.data.social_facebook || "",
        social_instagram: res.data.social_instagram || "",
        social_linkedin: res.data.social_linkedin || ""
      });
    } catch (error) {
      console.error("Error loading settings", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Impostazioni salvate");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e, assetType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande (max 5MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", assetType);
      
      const res = await axios.post(`${API}/admin/site-settings/upload?asset_type=${assetType}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (assetType === "logo") {
        setSettings({ ...settings, logo_url: res.data.url });
      } else {
        setSettings({ ...settings, favicon_url: res.data.url });
      }
      
      toast.success(`${assetType === "logo" ? "Logo" : "Favicon"} caricato`);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Personalizzazione <span className="text-primary">Sito</span>
          </h1>
          <p className="text-sm text-muted-foreground">Logo, favicon e informazioni footer</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Logo & Favicon */}
          <div className="card-dashboard">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              <Image className="h-5 w-5 text-primary" />
              Logo e Favicon
            </h2>

            <div className="space-y-6">
              {/* Logo */}
              <div>
                <label className="input-label">Logo Sito</label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-16 border border-dashed border-border rounded flex items-center justify-center bg-muted/50">
                    {settings.logo_url ? (
                      <img src={settings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Image className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={(e) => handleUpload(e, "logo")} 
                        disabled={uploading} 
                      />
                      <Button variant="outline" asChild disabled={uploading}>
                        <span>
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                          Carica Logo
                        </span>
                      </Button>
                    </label>
                    {settings.logo_url && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setSettings({ ...settings, logo_url: "" })}
                        className="ml-2"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Consigliato: 200x60px, PNG o SVG</p>
                  </div>
                </div>
              </div>

              {/* Favicon */}
              <div>
                <label className="input-label">Favicon</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 border border-dashed border-border rounded flex items-center justify-center bg-muted/50">
                    {settings.favicon_url ? (
                      <img src={settings.favicon_url} alt="Favicon" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Globe className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*,.ico"
                        onChange={(e) => handleUpload(e, "favicon")} 
                        disabled={uploading} 
                      />
                      <Button variant="outline" asChild disabled={uploading}>
                        <span>
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                          Carica Favicon
                        </span>
                      </Button>
                    </label>
                    {settings.favicon_url && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setSettings({ ...settings, favicon_url: "" })}
                        className="ml-2"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Consigliato: 32x32px, ICO o PNG</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="card-dashboard">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Informazioni Footer
            </h2>

            <div className="space-y-4">
              <div>
                <label className="input-label">Email Contatto</label>
                <Input
                  type="email"
                  value={settings.footer_email}
                  onChange={(e) => setSettings({ ...settings, footer_email: e.target.value })}
                  placeholder="info@noleggiofurgoni.it"
                />
              </div>

              <div>
                <label className="input-label">Telefono</label>
                <Input
                  value={settings.footer_phone}
                  onChange={(e) => setSettings({ ...settings, footer_phone: e.target.value })}
                  placeholder="+39 02 1234567"
                />
              </div>

              <div>
                <label className="input-label">Indirizzo</label>
                <Input
                  value={settings.footer_address}
                  onChange={(e) => setSettings({ ...settings, footer_address: e.target.value })}
                  placeholder="Via Roma, 1"
                />
              </div>

              <div>
                <label className="input-label">Città</label>
                <Input
                  value={settings.footer_city}
                  onChange={(e) => setSettings({ ...settings, footer_city: e.target.value })}
                  placeholder="20100 Milano (MI)"
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="card-dashboard lg:col-span-2">
            <h2 className="font-bold mb-4">Link Social (opzionale)</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="input-label">Facebook</label>
                <Input
                  value={settings.social_facebook}
                  onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
                  placeholder="https://facebook.com/..."
                />
              </div>

              <div>
                <label className="input-label">Instagram</label>
                <Input
                  value={settings.social_instagram}
                  onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div>
                <label className="input-label">LinkedIn</label>
                <Input
                  value={settings.social_linkedin}
                  onChange={(e) => setSettings({ ...settings, social_linkedin: e.target.value })}
                  placeholder="https://linkedin.com/..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salva Impostazioni
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
